/**
 * useBulkResolutionCache — optimistic-removal / restore-on-failure list-cache
 * orchestration for the AI Actions list. Extracted from useBulkResolution
 * alongside useBulkResolutionCompletion and useBulkResolutionSelection so the
 * cache choreography is reviewable in isolation.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import type {
  ProposalListItem,
  ProposalsListResponse,
} from '@/lib/proposals/core/types';
import { proposalQueryKeys } from '@/lib/proposals/query/keys';
import {
  addResolvingTombstones,
  clearResolvingTombstones,
} from '@/lib/proposals/query/resolvingTombstones';

export interface BulkResolutionCache {
  /** Tombstones the ids for the resolve round-trip and removes them from
      every list cache immediately. */
  beginOptimisticRemoval: (idsToRemove: Set<number>) => void;
  /** Re-adds failed items to every list cache, restoring their
      filter-tag counts. */
  restoreFailedItems: (failedItems: ProposalListItem[]) => void;
  /** Marks list caches stale WITHOUT refetching — see call site comment. */
  markListsStaleWithoutRefetch: () => void;
  /** Clears the tombstones once the resolve round-trip has settled. */
  endOptimisticRemoval: (idsToRemove: Set<number>) => void;
}

export function useBulkResolutionCache(
  orgId: number | undefined
): BulkResolutionCache {
  const queryClient = useQueryClient();

  const beginOptimisticRemoval = useCallback(
    (idsToRemove: Set<number>) => {
      // Tombstone the ids for the resolve round-trip: any list refetch in
      // this window (an autosave onSuccess invalidation, a WS repagination
      // for an unrelated proposal) reads the server's still-pending state
      // and would resurrect the rows we're about to remove — the
      // disappear → reappear → disappear flicker. The list queryFn filters
      // tombstoned ids out of every response until endOptimisticRemoval
      // clears them (see resolvingTombstones.ts).
      addResolvingTombstones(idsToRemove);

      if (!orgId) {
        return;
      }

      // Optimistically remove ALL items from the list immediately
      queryClient.setQueriesData<ProposalsListResponse>(
        { queryKey: proposalQueryKeys.lists(orgId) },
        oldData => {
          if (!oldData?.results) {
            return oldData;
          }

          const removedActions = oldData.results.filter(a =>
            idsToRemove.has(a.id)
          );
          const newResults = oldData.results.filter(
            a => !idsToRemove.has(a.id)
          );

          // Update filter_tag_counts for all removed actions
          const newFilterTagCounts = { ...oldData.filter_tag_counts };
          for (const removed of removedActions) {
            if (removed.filter_tags) {
              for (const tag of removed.filter_tags) {
                if (newFilterTagCounts[tag] !== undefined) {
                  newFilterTagCounts[tag] = Math.max(
                    0,
                    (newFilterTagCounts[tag] ?? 0) - 1
                  );
                }
              }
            }
          }

          return {
            ...oldData,
            results: newResults,
            count: Math.max(0, (oldData.count ?? 0) - removedActions.length),
            filter_tag_counts: newFilterTagCounts,
          };
        }
      );
    },
    [orgId, queryClient]
  );

  const restoreFailedItems = useCallback(
    (failedItems: ProposalListItem[]) => {
      if (!orgId || failedItems.length === 0) {
        return;
      }
      queryClient.setQueriesData<ProposalsListResponse>(
        { queryKey: proposalQueryKeys.lists(orgId) },
        oldData => {
          // The `results` guard also skips the history views' infinite
          // caches (InfiniteData under the same lists prefix) — writing
          // a flat `results` array into those would corrupt them.
          if (!oldData?.results) {
            return oldData;
          }
          return {
            ...oldData,
            results: [...oldData.results, ...failedItems],
            count: (oldData.count ?? 0) + failedItems.length,
            filter_tag_counts: (() => {
              const restored = { ...(oldData.filter_tag_counts ?? {}) };
              for (const item of failedItems) {
                if (item.filter_tags) {
                  for (const tag of item.filter_tags) {
                    // INCREMENT (not decrement) — we're restoring items, not removing them
                    restored[tag] = (restored[tag] ?? 0) + 1;
                  }
                }
              }
              return restored;
            })(),
          };
        }
      );
    },
    [orgId, queryClient]
  );

  const markListsStaleWithoutRefetch = useCallback(() => {
    if (!orgId) {
      return;
    }
    // Mark list caches stale WITHOUT refetching. Approval submits in the
    // background (submitApprovalInBackground), so an eager refetch here
    // races the server-side commit: the response still contains the
    // just-approved proposal, resurrecting the optimistically-removed row
    // for a beat until the confirmation path's invalidate removes it
    // again (the disappear→reappear→disappear flash). The confirmation
    // paths (submitApprovalInBackground / the reject mutation / WS
    // proposal events) all refresh the lists once the resolution has
    // committed.
    queryClient.invalidateQueries({
      queryKey: proposalQueryKeys.lists(orgId),
      refetchType: 'none',
    });
  }, [orgId, queryClient]);

  const endOptimisticRemoval = useCallback((idsToRemove: Set<number>) => {
    // Requests have settled either way: fulfilled ids are gone from the
    // server's pending lists (refetches now exclude them naturally), and
    // failed ids were restored above — the tombstone must not keep
    // suppressing them.
    clearResolvingTombstones(idsToRemove);
  }, []);

  return useMemo(
    () => ({
      beginOptimisticRemoval,
      restoreFailedItems,
      markListsStaleWithoutRefetch,
      endOptimisticRemoval,
    }),
    [
      beginOptimisticRemoval,
      restoreFailedItems,
      markListsStaleWithoutRefetch,
      endOptimisticRemoval,
    ]
  );
}
